<template>
  <div class="app-container padding">
    <el-row :gutter="20">
      <!-- 左侧报修类型 -->
      <el-col :span="3">
        <el-card>
          <div class="status-list">
            <div
              v-for="(item, index) in orderTypes"
              :key="index"
              class="status-item"
              :class="{ 'status-active': conditions.orderType === item.orderType }"
              @click="switchOrderType(item)"
            >
              {{ item.name }}
            </div>
          </div>
        </el-card>
      </el-col>

      <!-- 右侧内容 -->
      <el-col :span="21">
        <!-- 筛选条件 -->
        <el-card class="box-card">
          <div slot="header" class="header-with-role">
            <span>{{ $t('repairPoolManage.queryConditions') }}</span>
            <div class="role-switch">
              <el-dropdown trigger="click" @command="handleRoleChange">
                <el-button class="role-switch-btn" type="primary" size="mini">
                  <i class="el-icon-user-solid role-switch-icon" />
                  {{ $t('repairPoolManage.switchRole') }}：{{ currentRoleName || $t('repairPoolManage.roleDefault') }}
                  <i class="el-icon-arrow-down el-icon--right" />
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item v-if="roleList.length === 0" disabled>
                    {{ $t('repairPoolManage.roleEmpty') }}
                  </el-dropdown-item>
                  <el-dropdown-item v-for="role in roleList" :key="role.key" :command="role.key">
                    {{ role.name }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
          </div>
          </div>
          <!-- 查询条件：单排展示 -->
          <el-row :gutter="20" class="condition-row">
            <el-col v-if="!isAdmin" :xs="24" :sm="12" :md="10" :lg="8" :xl="6" class="tabtype-radio-col">
              <el-radio-group
                v-model="conditions.tabType"
                class="tabtype-radio-group"
                @change="handleTabTypeChange"
              >
                <el-radio
                  v-for="(item, index) in tabTypes"
                  :key="index"
                  :label="item.tabType"
                  class="tabtype-radio-item"
                >
                  {{ item.name }}
                </el-radio>
              </el-radio-group>
            </el-col>
            <el-col :xs="12" :sm="8" :md="6" :lg="5" :xl="4">
              <el-input v-model.trim="conditions.orderNo" :placeholder="$t('repairPoolManage.workOrderCodePlaceholder')"
                clearable />
            </el-col>
            <el-col :xs="12" :sm="8" :md="6" :lg="5" :xl="4">
              <el-input v-model.trim="conditions.initiatorName" :placeholder="$t('repairPoolManage.repairPersonPlaceholder')"
                clearable />
            </el-col>
            <el-col v-if="isAdmin" :xs="12" :sm="8" :md="6" :lg="5" :xl="4">
              <el-select
                v-model="conditions.status"
                :placeholder="$t('repairPoolManage.statusPlaceholder')"
                style="width:100%"
                clearable
              >
                <el-option
                  v-for="(item, index) in states"
                  :key="index"
                  :label="item.name"
                  :value="item.statusCd"
                />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="8" :md="6" :lg="4" :xl="4">
              <el-button type="primary" @click="queryRepairPool">{{ $t('repairPoolManage.search') }}</el-button>
              <el-button @click="resetRepairPool">{{ $t('repairPoolManage.reset') }}</el-button>
            </el-col>
          </el-row>
        </el-card>

        <!-- 报修工单池 -->
        <el-card class="box-card">
          <div slot="header" class="clearfix flex justify-between">
            <span>{{ $t('repairPoolManage.repairOrderPool') }}</span>
          </div>

          <el-table v-loading="loading" :data="repairPools" border style="width: 100%">
            <el-table-column prop="orderNo" :label="$t('repairPoolManage.workOrderCode')" align="center" width="160" />
            <el-table-column :label="$t('repairPoolManage.location')" align="center">
              <template slot-scope="scope">
                {{ getLocationText(scope.row) }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('repairPoolManage.repairType')" align="center">
              <template slot-scope="scope">
                {{ getOrderTypeText(scope.row.orderType) }}
              </template>
            </el-table-column>
            <el-table-column prop="initiatorName" :label="$t('repairPoolManage.repairPerson')" align="center" />
            <el-table-column prop="initiatorPhone" :label="$t('repairPoolManage.contact')" align="center" />
            <el-table-column prop="bookingTime" :label="$t('repairPoolManage.appointmentTime')" align="center" min-width="150" />
            <el-table-column prop="createTime" :label="$t('repairPoolManage.submissionTime')" align="center" min-width="150" />
            <el-table-column prop="finishTime" :label="$t('repairPoolManage.completionTime')" align="center" min-width="150">
              <template slot-scope="scope">
                {{ scope.row.finishTime || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('repairPoolManage.status')" align="center">
              <template slot-scope="scope">
                <el-tag :type="getStatusTagType(scope.row.status)" size="small">
                  {{ formatStatusName(scope.row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column :label="$t('repairPoolManage.operation')" align="center" width="220">
              <template slot-scope="scope">
                <el-button v-if="hasPrivilege('502021012067300023') && ['1000', '1200'].includes(scope.row.state)"
                  size="mini" @click="openEditModal(scope.row)">
                  {{ $t('repairPoolManage.modify') }}
                </el-button>

                <el-button v-if="hasPrivilege('502019101946430010') && scope.row.state === '1000'" size="mini"
                  type="primary" @click="openDispatchModal(scope.row)">
                  {{ $t('repairPoolManage.dispatch') }}
                </el-button>

                <el-button
                  v-if="hasPrivilege('502021012099350016') && scope.row.state === '1000' && scope.row.repairWay === '100'"
                  size="mini" type="success" @click="grabOrder(scope.row)">
                  {{ $t('repairPoolManage.grabOrder') }}
                </el-button>

                <!-- 未处理列表显示"立即处理"按钮（红底白字），已处理列表显示"详情"按钮 -->
                <el-button 
                  v-if="conditions.tabType === '1'"
                  size="mini" 
                  type="danger" 
                  style="margin: 2px;"
                  @click="openDetail(scope.row)">
                  立即处理
                </el-button>
                <el-button 
                  v-else
                  size="mini" 
                  type="primary" 
                  style="margin: 2px;"
                  @click="openDetail(scope.row)">
                  查看详情
                </el-button>

                <!-- 预览按钮 - 我发起的不显示 -->
                <el-button 
                  v-if="conditions.tabType !== '0'"
                  size="mini" 
                  type="warning" 
                  style="margin: 2px;"
                  @click="openPreviewModal(scope.row)">
                  预览维修表
                </el-button>

                <el-button v-if="hasPrivilege('502021012051410024') && ['1000', '1200'].includes(String(scope.row.status))"
                  size="mini" type="danger" @click="openDeleteModal(scope.row.id || scope.row.orderNo)">
                  {{ $t('repairPoolManage.delete') }}
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <!-- 分页 -->
          <el-pagination background layout="total, sizes, prev, pager, next, jumper" :current-page="page.current"
            :page-sizes="[10, 20, 30, 50]" :page-size="page.size" :total="page.total" @size-change="handleSizeChange"
            @current-change="handleCurrentChange" />
        </el-card>
      </el-col>
    </el-row>

    <!-- 组件 -->
    <dispatch-repair ref="dispatchRepair" @success="fetchData" />
    <end-repair ref="endRepair" @success="fetchData" />
    <edit-owner-repair ref="editOwnerRepair" @success="fetchData" />
    <delete-owner-repair ref="deleteOwnerRepair" @success="fetchData" />
    <staff-repair ref="staffRepair" @success="fetchData" />
    
    <!-- 修改工单弹窗 -->
    <el-dialog 
      title="修改工单" 
      :visible.sync="editRepairDialogVisible" 
      width="80%" 
      :close-on-click-modal="false"
      @close="handleEditDialogClose">
      <edit-repair-order 
        v-if="editRepairDialogVisible"
        :repairId="editingRepairId"
        @success="handleEditSuccess" 
        @cancel="handleEditCancel" />
    </el-dialog>
    
    <!-- 预览组件 -->
    <preview-repair-form
      :visible="previewDialogVisible"
      :repair-detail="previewRepairDetail"
      @close="closePreviewModal"
    />

    <!-- 工单详情弹窗 -->
    <el-dialog title="工单详情" :visible.sync="detailDialogVisible" width="80%" :close-on-click-modal="false">
      <div v-loading="detailLoading">
        <div v-if="repairDetail" class="repair-detail-content">
          <!-- 工单详情 -->
          <el-card class="detail-section" shadow="never">
            <div slot="header" class="section-title">工单详情</div>
            <el-descriptions :column="2" border>
              <el-descriptions-item label="工单号">{{ repairDetail.orderNo || '-' }}</el-descriptions-item>
              <el-descriptions-item label="状态">
                <el-tag :type="getStatusTagType(repairDetail.status)" size="small">
                  {{ formatStatusName(repairDetail.status) }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="报修类型">
                {{ getOrderTypeText(repairDetail.orderType) }}
              </el-descriptions-item>
              <el-descriptions-item label="报修部位">{{ repairDetail.itemName || '-' }}</el-descriptions-item>
              <el-descriptions-item label="所属专业">{{ repairDetail.professionName || '-' }}</el-descriptions-item>
              <el-descriptions-item label="报修人">
                {{ repairDetail.initiatorName }}({{ repairDetail.initiatorPhone }})
              </el-descriptions-item>
              <el-descriptions-item label="位置" :span="2">{{ getDetailLocationText() }}</el-descriptions-item>
              <el-descriptions-item label="创建时间">{{ repairDetail.createTime || '-' }}</el-descriptions-item>
              <el-descriptions-item label="预约时间">{{ repairDetail.bookingTime || '-' }}</el-descriptions-item>
              <el-descriptions-item label="完成时间" v-if="repairDetail.finishTime">{{ repairDetail.finishTime }}</el-descriptions-item>
              <el-descriptions-item label="保修期" v-if="repairDetail.warrantyType && repairDetail.warrantyType !== '0'">
                {{ getWarrantyTypeText(repairDetail.warrantyType) }}
              </el-descriptions-item>
              <el-descriptions-item label="报修内容" :span="2">{{ repairDetail.problemDesc || '-' }}</el-descriptions-item>
              <el-descriptions-item label="报修图片" :span="2" v-if="getImageList(repairDetail.problemImgs).length > 0">
                <div class="image-list">
                  <el-image
                    v-for="(img, index) in getImageList(repairDetail.problemImgs)"
                    :key="index"
                    :src="img"
                    :preview-src-list="getImageList(repairDetail.problemImgs)"
                    fit="cover"
                    class="detail-image"
                  />
  </div>
              </el-descriptions-item>
              <el-descriptions-item label="已支付维修金额" v-if="repairDetail.payStatus && repairDetail.payStatus !== '0' && repairDetail.payStatus !== 0">
                ¥{{ repairDetail.repairMaterialFee || '0.00' }}
              </el-descriptions-item>
              <el-descriptions-item label="支付状态" v-if="repairDetail.payStatus && repairDetail.payStatus !== '0' && repairDetail.payStatus !== 0">
                <el-tag :type="repairDetail.payStatus === '2' || repairDetail.payStatus === 2 ? 'success' : 'warning'" size="small">
                  {{ getPayStatusText(repairDetail.payStatus) }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="驳回理由" :span="2" v-if="repairDetail.rejectText">
                <span style="color: #F56C6C;">{{ repairDetail.rejectText }}</span>
              </el-descriptions-item>
            </el-descriptions>
          </el-card>

          <!-- 处理详情（大标题，内部包含各处理信息块） -->
          <el-card
            class="detail-section"
            shadow="never"
            v-if="(repairDetail.handlerName || repairDetail.handlerPhone) || displayMaterialList.length > 0 || signatureList.length > 0 || getProcessImageList(repairDetail.repairProcessImgs).length > 0 || supplierInfoList.length > 0 || quoteInfo.num || quoteInfo.remark || auditControlInfo.controlNum || auditControlInfo.controlRemark || getAcceptanceImageList(repairDetail.repairAfterImgs).length > 0"
          >
            <div slot="header" class="section-title">处理详情</div>

            <!-- 维修人员信息 -->
            <div v-if="repairDetail.handlerName || repairDetail.handlerPhone" class="sub-section">
              <div class="sub-section-title">维修人员信息</div>
              <el-descriptions :column="2" border>
                <el-descriptions-item label="维修人员">
                  {{ repairDetail.handlerName || '-' }}{{ repairDetail.handlerPhone ? `（${repairDetail.handlerPhone}）` : '' }}
                </el-descriptions-item>
              </el-descriptions>
            </div>

            <!-- 使用材料信息 -->
            <div v-if="displayMaterialList.length > 0" class="sub-section">
              <div class="sub-section-title">使用材料信息</div>
              <el-table :data="displayMaterialList" border>
                <el-table-column prop="name" label="材料名称" align="center" />
                <el-table-column prop="num" label="数量" align="center" />
                <el-table-column prop="unit" label="单位" align="center" />
                <el-table-column prop="price" label="单价" align="center">
                  <template slot-scope="scope">
                    ¥{{ scope.row.price }}
                  </template>
                </el-table-column>
                <el-table-column label="小计" align="center">
                  <template slot-scope="scope">
                    ¥{{ calculateMaterialTotal(scope.row) }}
                  </template>
                </el-table-column>
              </el-table>
              <div class="material-summary">
                <span class="summary-label">合计：</span>
                <span class="summary-price">¥{{ repairDetail.repairMaterialFee || '0.00' }}</span>
              </div>
            </div>

            <!-- 维修图片信息（回显） -->
            <div v-if="getProcessImageList(repairDetail.repairProcessImgs).length > 0" class="sub-section">
              <div class="sub-section-title">维修图片信息</div>
              <div class="image-list">
                <el-image
                  v-for="(img, index) in getProcessImageList(repairDetail.repairProcessImgs)"
                  :key="index"
                  :src="img"
                  :preview-src-list="getProcessImageList(repairDetail.repairProcessImgs)"
                  fit="cover"
                  class="detail-image"
                />
              </div>
            </div>

            <!-- 签名信息（回显） -->
            <div v-if="signatureList.length > 0" class="sub-section">
              <div class="sub-section-title">签名信息</div>
              <div
                v-for="(signature, index) in signatureList"
                :key="index"
                class="signature-record-item"
              >
                <div class="signature-role-name">
                  {{ signature.roleName }}签名：
                </div>
                <div class="signature-image-wrapper">
                  <el-image
                    :src="signature.img"
                    :preview-src-list="[signature.img]"
                    fit="contain"
                    class="signature-image"
                  />
                </div>
              </div>
            </div>
            
            <!-- 供应商信息（回显） -->
            <div v-if="supplierInfoList.length > 0" class="sub-section">
              <div class="sub-section-title">供应商信息</div>
              <el-table :data="supplierInfoList" border class="supplier-info-table">
                <el-table-column prop="name" label="供应商名称" align="center" min-width="160">
                  <template slot-scope="scope">
                    {{ scope.row.name || '-' }}
                  </template>
                </el-table-column>
                <el-table-column prop="rsId" label="编号" align="center" min-width="180">
                  <template slot-scope="scope">
                    {{ scope.row.rsId || '-' }}
                  </template>
                </el-table-column>
                <el-table-column prop="contactName" label="联系人" align="center" min-width="120">
                  <template slot-scope="scope">
                    {{ scope.row.contactName || '-' }}
                  </template>
                </el-table-column>
                <el-table-column prop="tel" label="电话" align="center" min-width="140">
                  <template slot-scope="scope">
                    {{ scope.row.tel || '-' }}
                  </template>
                </el-table-column>
              </el-table>
            </div>
            
            <!-- 客服报价（供应商报价信息回显） -->
            <div v-if="quoteInfo.num || quoteInfo.remark" class="sub-section">
              <div class="sub-section-title">客服报价</div>
              <el-descriptions :column="2" border>
                <el-descriptions-item label="报价金额" v-if="quoteInfo.num">
                  <span class="quote-price">¥{{ quoteInfo.num }}</span>
                </el-descriptions-item>
                <el-descriptions-item label="报价说明" :span="2" v-if="quoteInfo.remark">
                  <div class="remark-content">{{ quoteInfo.remark }}</div>
                </el-descriptions-item>
              </el-descriptions>
            </div>

            <!-- 审计控价信息（回显） -->
            <div v-if="auditControlInfo.controlNum || auditControlInfo.controlRemark" class="sub-section">
              <div class="sub-section-title">审计控价信息</div>
              <el-descriptions :column="2" border>
                <el-descriptions-item label="控价金额" v-if="auditControlInfo.controlNum">
                  <span class="control-price">¥{{ auditControlInfo.controlNum }}</span>
                </el-descriptions-item>
                <el-descriptions-item label="控价说明" :span="2" v-if="auditControlInfo.controlRemark">
                  <div class="remark-content">{{ auditControlInfo.controlRemark }}</div>
                </el-descriptions-item>
              </el-descriptions>
            </div>

            <!-- 验收图片（回显） -->
            <div v-if="getAcceptanceImageList(repairDetail.repairAfterImgs).length > 0" class="sub-section">
              <div class="sub-section-title">验收图片</div>
              <div class="image-list">
                <el-image
                  v-for="(img, index) in getAcceptanceImageList(repairDetail.repairAfterImgs)"
                  :key="index"
                  :src="img"
                  :preview-src-list="getAcceptanceImageList(repairDetail.repairAfterImgs)"
                  fit="cover"
                  class="detail-image"
                />
              </div>
            </div>
          </el-card>

          <!-- 业主评价 -->
          <el-card class="detail-section" shadow="never" v-if="repairDetail.rateInfo">
            <div class="rate-content">
              <p class="rate-text">{{ repairDetail.rateInfo }}</p>
              <div class="image-list" v-if="getRateImageList(repairDetail.rateImgs).length > 0">
                <el-image
                  v-for="(img, index) in getRateImageList(repairDetail.rateImgs)"
                  :key="index"
                  :src="img"
                  :preview-src-list="getRateImageList(repairDetail.rateImgs)"
                  fit="cover"
                  class="detail-image"
                />
              </div>
            </div>
          </el-card>

          <!-- 保修期选择区域 - 仅“未处理”列表进入且待派单状态且非公区报修时显示（参考小程序） -->
          <div v-if="repairDetail && isPendingTab() && isAction('1000') && !isPublicAreaRepair()" class="action-section">
            <el-card shadow="never" class="action-card">
              <div class="action-title">保修期选择</div>
              <div class="action-content">
                <el-radio-group v-model="warrantyType" @change="onWarrantyTypeChange">
                  <el-radio :label="true">保修期内</el-radio>
                  <el-radio :label="false">保修期外</el-radio>
                </el-radio-group>
              </div>
              <!-- 保修期内也需要提交按钮（参考小程序：仅更新保修期，不走流程） -->
              <div v-if="warrantyType !== null && warrantyType === true" class="submit-section submit-section-inline">
                <el-button type="primary" @click="handleSubmit" :loading="submitLoading">立即提交</el-button>
                <el-button type="warning" @click="handleChangeToComplaintFromDetail" :loading="submitLoading">转投诉单</el-button>
                <el-button v-if="canEditRepair" type="danger" @click="handleEditRepair">修改工单</el-button>
              </div>
            </el-card>
          </div>

          <!-- 操作区域 - 根据不同动作显示不同内容 -->
          <div v-if="isPendingTab() && shouldShowActionSection() && repairDetail" class="action-section">
            <el-card shadow="never" class="action-card">
              <div class="action-title">{{ getActionTitle() }}</div>
              <el-form :model="actionForm" label-width="100px">
              
              <!-- 指派人员 (statusCd: 1000) -->
              <div v-if="isAction('1000') && (warrantyType === false || isPublicAreaRepair())" class="action-content">
                <el-form-item class="no-item-label">
                  <el-radio-group v-model="selectedStaffId" class="staff-radio-group">
                    <el-radio
                      v-for="staff in staffList"
                      :key="staff.id"
                      :label="staff.id"
                      class="staff-radio-item">
                      {{ staff.name }}{{ staff.tel ? `（${staff.tel}）` : '' }}
                    </el-radio>
                  </el-radio-group>
                </el-form-item>
              </div>
              
              <!-- 勘察 (statusCd: 1001) -->
              <div v-if="isAction('1001')" class="action-content">
                <el-form-item label="是否能修" class="can-repair-item">
                  <el-radio-group v-model="canRepair" class="can-repair-radios">
                    <el-radio label="1">能修</el-radio>
                    <el-radio label="0">不能修</el-radio>
                  </el-radio-group>
                </el-form-item>
                
                <!-- 材料选择区域 - 仅选择"能修"时显示 -->
                <div v-if="canRepair === '1'" class="material-section">
                  <el-form-item label="所需材料">
                    <div class="material-toolbar">
                      <el-input
                        v-model="materialSearchText"
                        placeholder="搜索材料名称"
                        clearable
                        size="small"
                        class="material-search-input"
                        @keyup.enter.native="loadMaterialList"
                        @clear="handleMaterialSearchClear"
                      >
                        <el-button slot="append" icon="el-icon-search" @click="loadMaterialList" />
                      </el-input>
                    </div>

                    <div class="material-table-wrapper">
                      <el-table
                        v-loading="materialLoading"
                        :data="materialList"
                        border
                        size="small"
                        class="material-table"
                        @selection-change="handleMaterialSelectionChange"
                      >
                        <el-table-column type="selection" width="50" />
                        <el-table-column prop="resName" label="材料名称" min-width="160" />
                        <el-table-column prop="resCode" label="编号" min-width="120" />
                        <el-table-column prop="spec" label="规格" min-width="120" />
                        <el-table-column prop="miniStock" label="库存" width="80" />
                        <el-table-column prop="price" label="单价(￥)" width="100">
                          <template slot-scope="scope">
                            {{ scope.row.price || 0 }}
                          </template>
                        </el-table-column>
                        <el-table-column label="使用数量" width="150">
                          <template slot-scope="scope">
                            <div class="material-quantity-cell">
                              <el-input-number
                                v-model="scope.row.quantity"
                                :min="1"
                                size="small"
                                :disabled="!isMaterialRowSelected(scope.row)"
                                @change="onMaterialQuantityChange(scope.row)"
                              />
                              <span class="material-unit">
                                {{ scope.row.miniUnitCodeName || '个' }}
                              </span>
                            </div>
                          </template>
                        </el-table-column>
                      </el-table>
                    </div>

                    <div class="material-footer">
                      <div class="material-summary">
                        <span class="summary-label">合计：</span>
                        <span class="summary-price">¥{{ calculateMaterialTotalPrice() }}</span>
                      </div>
                      <el-pagination
                        v-if="materialTotal > 0"
                        background
                        layout="prev, pager, next"
                        :page-size="materialPageSize"
                        :current-page.sync="materialPage"
                        :total="materialTotal"
                        @current-change="handleMaterialPageChange"
                      />
                    </div>
                  </el-form-item>
                </div>
              </div>
              
              <!-- 填写报价(审价)/控价 (statusCd: 1002) -->
              <div v-if="isAction('1002')" class="action-content">
                <el-form-item :label="repairDetail.quoteInfo ? '控价金额' : '报价金额'">
                  <el-input v-model="quoteInputAmount" type="number" placeholder="请输入金额">
                    <template slot="append">元</template>
                  </el-input>
                </el-form-item>
                <el-form-item :label="repairDetail.quoteInfo ? '控价说明' : '报价说明'">
                  <el-input
                    v-model="quoteInputRemark"
                    type="textarea"
                    :rows="3"
                    placeholder="请输入说明"
                    maxlength="200">
                  </el-input>
                </el-form-item>
              </div>
              
              <!-- 维修 (statusCd: 1003) -->
              <div v-if="isAction('1003')" class="action-content">
                <el-form-item label="维修图片">
                  <el-upload
                    action=""
                    list-type="picture-card"
                    :file-list="repairImages"
                    :on-preview="handlePictureCardPreview"
                    :on-remove="handleRepairRemove"
                    :auto-upload="true"
                    :http-request="uploadRepairImage">
                    <i class="el-icon-plus"></i>
                  </el-upload>
                </el-form-item>
              </div>
              
            <!-- 审核 (statusCd: 1004) -->
            <div v-if="isAction('1004')" class="action-content">
              <el-form-item label="您的签名" v-if="repairDetail.isSign === 1 || repairDetail.isSign === '1'">
                <el-image
                  v-if="selectedSignature"
                  :src="selectedSignature"
                  :preview-src-list="[selectedSignature]"
                  fit="contain"
                  style="width: 200px; margin-left: 10px; cursor: pointer;"
                />
                <span v-else style="color: #999; margin-left: 10px;">暂无签名，请在个人中心上传</span>
              </el-form-item>
              <div class="action-buttons">
                <el-button type="danger" @click="showRejectModal = true">驳回</el-button>
                <el-button type="success" @click="handleApprove">同意</el-button>
              </div>
            </div>
              
            <!-- 验收 (statusCd: 1005) -->
            <div v-if="isAction('1005')" class="action-content">
              <el-form-item label="验收图片">
                <el-upload
                  action=""
                  list-type="picture-card"
                  :file-list="acceptanceImages"
                  :on-preview="handlePictureCardPreview"
                  :on-remove="handleAcceptanceRemove"
                  :auto-upload="true"
                  :http-request="uploadAcceptanceImage">
                  <i class="el-icon-plus"></i>
                </el-upload>
              </el-form-item>
              <el-form-item label="您的签名" v-if="repairDetail.isSign === 1 || repairDetail.isSign === '1'">
                <el-image
                  v-if="selectedSignature"
                  :src="selectedSignature"
                  :preview-src-list="[selectedSignature]"
                  fit="contain"
                  style="width: 200px; margin-left: 10px; cursor: pointer;"
                />
                <span v-else style="color: #999; margin-left: 10px;">暂无签名，请在个人中心上传</span>
              </el-form-item>
              <div class="action-buttons">
                <el-button type="danger" @click="showRejectModal = true">验收拒绝</el-button>
                <el-button type="success" @click="handleApprove">验收同意</el-button>
              </div>
            </div>
              
              <!-- 指派供应商 (statusCd: 1006) -->
              <div v-if="isAction('1006')" class="action-content">
                <div class="sub-section">
                  <div class="sub-section-title">供应商信息</div>
                  <el-form-item label="选择供应商">
                    <div class="supplier-section">
                      <div class="supplier-toolbar">
                        <el-input
                          v-model="supplierSearchText"
                          placeholder="搜索供应商名称"
                          clearable
                          size="small"
                          class="supplier-search-input"
                          @keyup.enter.native="loadSupplierList"
                          @clear="handleSupplierSearchClear"
                        >
                          <el-button slot="append" icon="el-icon-search" @click="loadSupplierList" />
                        </el-input>
                      </div>

                      <el-table
                        ref="supplierTable"
                        v-loading="supplierLoading"
                        :data="supplierList"
                        border
                        size="small"
                        class="supplier-table"
                        @selection-change="handleSupplierSelectionChange"
                      >
                        <el-table-column type="selection" width="50" />
                        <el-table-column prop="supplierName" label="供应商名称" min-width="160" />
                        <el-table-column prop="rsId" label="编号" min-width="120" />
                        <el-table-column prop="contactName" label="联系人" min-width="120" />
                        <el-table-column prop="tel" label="电话" min-width="140" />
                      </el-table>

                      <div class="supplier-footer">
                        <div class="selected-suppliers" v-if="selectedSuppliers.length > 0">
                          <div
                            v-for="(supplier, index) in selectedSuppliers"
                            :key="supplier.rsId || index"
                            class="selected-supplier-item"
                          >
                            <div class="selected-supplier-row">
                              <span class="summary-label">供应商名称：</span>
                              <span class="selected-suppliers-text">{{ supplier.supplierName || supplier.name || '-' }}</span>
                            </div>
                            <div class="selected-supplier-row">
                              <span class="summary-label">编号：</span>
                              <span class="selected-suppliers-text">{{ supplier.rsId || '-' }}</span>
                            </div>
                            <div class="selected-supplier-row">
                              <span class="summary-label">联系人：</span>
                              <span class="selected-suppliers-text">{{ supplier.contactName || '-' }}</span>
                            </div>
                            <div class="selected-supplier-row">
                              <span class="summary-label">电话：</span>
                              <span class="selected-suppliers-text">{{ supplier.tel || '-' }}</span>
                            </div>
                          </div>
                        </div>
                        <el-pagination
                          v-if="supplierTotal > supplierPageSize"
                          background
                          layout="prev, pager, next"
                          :page-size="supplierPageSize"
                          :current-page.sync="supplierPage"
                          :total="supplierTotal"
                          @current-change="handleSupplierPageChange"
                        />
                      </div>
                    </div>
                  </el-form-item>
                </div>
              </div>
              
              <!-- 确认维修责任方 (statusCd: 1007) -->
              <div v-if="isAction('1007')" class="action-content">
                <el-form-item label="维修责任方" class="responsibility-item">
                  <el-radio-group v-model="responsibilityType">
                    <el-radio label="1">属于物业付费维修范围</el-radio>
                    <el-radio label="0">不在物业付费维修范围</el-radio>
                  </el-radio-group>
                </el-form-item>
                <el-form-item label="您的签名">
                  <div class="signature-select-wrapper">
                    <el-image
                      v-if="selectedSignature"
                      :src="selectedSignature"
                      :preview-src-list="[selectedSignature]"
                      fit="contain"
                      class="signature-preview-image"
                    />
                    <span v-else style="color: #999;">暂无签名，请在个人中心上传</span>
                  </div>
                </el-form-item>
              </div>
              
              <!-- 提交按钮 -->
              <div v-if="showSubmitButton()" class="submit-section submit-section-inline">
                <el-button type="primary" @click="handleSubmit" :loading="submitLoading">立即提交</el-button>
                <!-- 转投诉单按钮 - 仅未处理/待派单/非公区报修时显示 -->
                <el-button
                  v-if="repairDetail && isPendingTab() && isAction('1000') && !isPublicAreaRepair()"
                  type="warning"
                  @click="handleChangeToComplaintFromDetail"
                  :loading="submitLoading"
                >转投诉单</el-button>
                <el-button v-if="canEditRepair" type="danger" @click="handleEditRepair">修改工单</el-button>
              </div>
              </el-form>
            </el-card>
          </div>
        </div>
        <div v-else-if="!detailLoading" class="empty-detail">
          <el-empty description="暂无工单详情"></el-empty>
        </div>
      </div>
      <div slot="footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 驳回理由弹窗 -->
    <el-dialog title="驳回理由" :visible.sync="showRejectModal" width="500px">
      <el-input
        v-model="rejectReason"
        type="textarea"
        :rows="5"
        placeholder="请输入驳回理由"
        maxlength="200">
      </el-input>
      <div slot="footer">
        <el-button @click="showRejectModal = false">取消</el-button>
        <el-button type="danger" @click="handleReject">确认驳回</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listRepairPools, grabbingRepair, executeFlowProcess, getUserByCategoryId, saveOrUpdateRepairOrder, repairChangeComplaint } from '@/api/work/repairPoolManageApi'
import { getCommunityId } from '@/api/community/communityApi'
import { getUserId } from '@/api/user/userApi'
import { getStaffRoles } from '@/api/staff/staffDetailApi'
import { getRepairStatusText, getRepairStatusOptions } from '@/utils/repairUtil'
import { getDictList } from '@/api/dev/dictApi'
import { listResourceStores } from '@/api/resource/resourceStoreManageApi'
import { listResourceSuppliers } from '@/api/resource/resourceSupplierManageApi'
import { uploadImage as uploadOwnerImage } from '@/api/owner/ownerApi'
import { getSignPic } from '@/api/user/userInfoApi'
import DispatchRepair from '@/components/work/dispatchRepair'
import EndRepair from '@/components/work/endRepair'
import EditOwnerRepair from '@/components/work/editOwnerRepair'
import DeleteOwnerRepair from '@/components/work/deleteOwnerRepair'
import StaffRepair from '@/components/work/staffRepair'
import PreviewRepairForm from '@/components/work/PreviewRepairForm'
import EditRepairOrder from '@/components/work/EditRepairOrder'

export default {
  name: 'RepairPoolManage',
  components: {
    DispatchRepair,
    EndRepair,
    EditOwnerRepair,
    DeleteOwnerRepair,
    StaffRepair,
    PreviewRepairForm,
    EditRepairOrder
  },
  data() {
    return {
      loading: false,
      repairPools: [],
      orderTypes: [],
      tabTypes: [],
      states: [],
      roleList: [],
      currentRoleKey: '',
      currentRoleName: '',
      currentRoleRelCd: '',
      conditions: {
        orderType: '',
        tabType: '1',
        orderNo: '',
        initiatorName: '',
        status: ''
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      // 工单详情
      detailDialogVisible: false,
      repairDetail: null,
      detailLoading: false,
      
      // 预览功能
      previewDialogVisible: false,
      previewRepairDetail: null,
      displayMaterialList: [],
      signatureList: [],
      supplierInfoList: [],
      auditControlInfo: {
        controlNum: '',
        controlRemark: ''
      },
      quoteInfo: {
        num: '',
        remark: ''
      },
      // 处理工单相关
      dictList: [], // 字典列表（操作动作）
      dictLoading: false,
      matchedStatusCd: '', // 匹配到的状态码
      currentAction: '', // 当前动作名称
      // 操作表单数据
      actionForm: {},
      selectedStaff: '',
      selectedStaffId: '',
      staffList: [],
      canRepair: '', // 勘察：能修/不能修
      confirmedMaterials: [], // 确认的材料列表
      quoteAmount: '', // 报价金额
      quoteRemark: '', // 报价说明
      controlAmount: '', // 控价金额
      controlRemark: '', // 控价说明
      repairImages: [], // 维修图片
      acceptanceImages: [], // 验收图片
      selectedSuppliers: [], // 已选择的供应商
      responsibilityType: '', // 维修责任方
      selectedSignature: '', // 选中的签名（最终确认）
      signatureImage: '', // 弹窗中展示的签名图片
      rejectReason: '', // 驳回理由
      warrantyType: false, // 保修期类型：true-保修期内，false-保修期外，默认保修期外
      // 勘察-材料选择
      materialList: [], // 材料列表（选择材料时用）
      materialLoading: false,
      materialPage: 1,
      materialPageSize: 20,
      materialTotal: 0,
      materialSearchText: '',
      selectedMaterials: [], // 暂存已选择的材料（带数量）
      supplierList: [], // 供应商列表
      supplierLoading: false,
      supplierPage: 1,
      supplierPageSize: 20,
      supplierTotal: 0,
      supplierSearchText: '',
      showMaterialPicker: false,
      showSupplierPicker: false,
      showStaffPicker: false,
      showSignaturePicker: false,
      signatureLoading: false,
      showRejectModal: false,
      submitLoading: false,
      
      // 修改工单弹窗
      editRepairDialogVisible: false,
      editingRepairId: null
    }
  },
  computed: {
    // 判断是否是管理员（物业管理员权限组）
    isAdmin() {
      return this.currentRoleName && this.currentRoleName.includes('物业管理员权限组')
    },
    // 判断是否是客服或项目负责人
    canEditRepair() {
      if (!this.currentRoleName) return false
      return this.currentRoleName.includes('客服') || this.currentRoleName.includes('项目负责人')
    },
    // 报价/控价金额（用于v-model）
    quoteInputAmount: {
      get() {
        return (this.repairDetail && this.repairDetail.quoteInfo) ? this.controlAmount : this.quoteAmount
      },
      set(value) {
        if (this.repairDetail && this.repairDetail.quoteInfo) {
          this.controlAmount = value
        } else {
          this.quoteAmount = value
        }
      }
    },
    // 报价/控价说明（用于v-model）
    quoteInputRemark: {
      get() {
        return (this.repairDetail && this.repairDetail.quoteInfo) ? this.controlRemark : this.quoteRemark
      },
      set(value) {
        if (this.repairDetail && this.repairDetail.quoteInfo) {
          this.controlRemark = value
        } else {
          this.quoteRemark = value
        }
      }
    }
  },
  watch: {
    // 选择"能修"时自动加载材料列表
    canRepair(val) {
      if (val === '1' && this.materialList.length === 0) {
        this.materialPage = 1
        this.loadMaterialList()
      }
    },

    // 当前动作变为"指派供应商"（statusCd:1006）时，自动加载供应商列表
    matchedStatusCd(val) {
      if (val === '1006') {
        this.supplierPage = 1
        this.loadSupplierList()
      }
    },

    // 关闭弹窗时清除供应商勾选状态
    detailDialogVisible(val) {
      if (!val) {
        // 弹窗关闭时只清除勾选状态，保留供应商列表数据
        this.selectedSuppliers = []
        // 清除表格的勾选状态
        this.$nextTick(() => {
          if (this.$refs.supplierTable) {
            this.$refs.supplierTable.clearSelection()
          }
        })
      }
    }
  },
  async created() {
    this.initOrderTypes()
    this.initTabTypes()
    this.loadStates()
    await this.loadRoles()
    // 页面进入时预加载“操作动作”字典，避免每次点详情都请求
    this.loadDictList()
    // 预加载当前用户的通用签名，用于各个环节直接回显
    this.loadDefaultSignature()
    this.fetchData()
  },
  methods: {
    // 顶部“未处理 / 已处理 / 我发起的”切换时，直接刷新列表
    handleTabTypeChange() {
      // 切换时重置到第一页
      if (this.page && this.page.current) {
        this.page.current = 1
      }
      this.queryRepairPool()
    },

    // ===== 勘察材料选择相关 =====
    async loadMaterialList() {
      try {
        this.materialLoading = true
        const params = {
          communityId: getCommunityId(),
          page: this.materialPage,
          row: this.materialPageSize,
          resId: '',
          resCode: '',
          shId: '',
          parentRstId: '',
          rstId: '',
          rssId: '',
          isFixed: '',
          flag: '',
          userId: ''
        }
        if (this.materialSearchText && this.materialSearchText.trim()) {
          params.resName = this.materialSearchText.trim()
        }

        const res = await listResourceStores(params)
        const list = Array.isArray(res.resourceStores) ? res.resourceStores : (Array.isArray(res.data) ? res.data : [])
        this.materialTotal = res.total || 0

        // 初始化数量字段，保留已选数量
        list.forEach(item => {
          const existed = this.selectedMaterials.find(m => m.resId === item.resId)
          if (existed) {
            this.$set(item, 'quantity', existed.quantity)
          } else if (item.quantity == null) {
            this.$set(item, 'quantity', 1)
          }
        })

        this.materialList = list
      } catch (e) {
        console.log('加载材料列表失败:', e)
        this.$message.error(e.message || '加载材料列表失败')
      } finally {
        this.materialLoading = false
      }
    },

    // ===== 供应商选择相关（指派供应商） =====
    async loadSupplierList() {
      try {
        this.supplierLoading = true
        const params = {
          page: this.supplierPage,
          row: this.supplierPageSize,
          communityId: getCommunityId()
        }
        if (this.supplierSearchText && this.supplierSearchText.trim()) {
          params.supplierName = this.supplierSearchText.trim()
        }

        const res = await listResourceSuppliers(params)
        const data = res && (res.data || res.resourceSuppliers || [])
        this.supplierList = Array.isArray(data) ? data : []
        this.supplierTotal = res && res.total ? res.total : 0
      } catch (e) {
        console.log('加载供应商列表失败:', e)
        this.$message.error(e.message || '加载供应商列表失败')
      } finally {
        this.supplierLoading = false
      }
    },

    handleSupplierSearchClear() {
      this.supplierPage = 1
      this.loadSupplierList()
    },

    handleSupplierPageChange(page) {
      this.supplierPage = page
      this.loadSupplierList()
    },

    handleSupplierSelectionChange(selection) {
      // 直接保存完整行数据，提交时按需要组装
      this.selectedSuppliers = Array.isArray(selection) ? selection.slice() : []
    },

    handleMaterialSearchClear() {
      this.materialPage = 1
      this.loadMaterialList()
    },

    handleMaterialPageChange(page) {
      this.materialPage = page
      this.loadMaterialList()
    },

    handleMaterialSelectionChange(selection) {
      this.selectedMaterials = selection.map(row => {
        const quantity = row.quantity && row.quantity > 0 ? row.quantity : 1
        return {
          resId: row.resId,
          resName: row.resName,
          price: row.price || 0,
          miniUnitCodeName: row.miniUnitCodeName || '个',
          quantity
        }
      })
      this.syncConfirmedMaterials()
    },

    onMaterialQuantityChange(row) {
      // 更新已选材料中的数量
      const target = this.selectedMaterials.find(m => m.resId === row.resId)
      if (target) {
        target.quantity = row.quantity && row.quantity > 0 ? row.quantity : 1
        this.syncConfirmedMaterials()
      }
    },

    isMaterialRowSelected(row) {
      return this.selectedMaterials.some(m => m.resId === row.resId)
    },

    syncConfirmedMaterials() {
      this.confirmedMaterials = (this.selectedMaterials || []).map(item => ({
        id: item.resId,
        name: item.resName,
        num: item.quantity || 1,
        price: item.price || 0,
        unit: item.miniUnitCodeName || '个'
      }))
    },

    // ===== 签名相关：预加载通用签名并在各环节直接回显 =====
    async loadDefaultSignature() {
      try {
        const userStr = localStorage.getItem('user') || '{}'
        const user = JSON.parse(userStr)
        const userId = user.userId || user.staffId || ''
        if (!userId) {
          // 无法获取用户信息时，直接跳过
          return
        }

        this.signatureLoading = true

        const res = await getSignPic({ userId })
        const signPic =
          (res && res.data && (res.data.signPic || res.data.url)) ||
          res.signPic ||
          res.url

        if (!signPic) {
          this.signatureImage = ''
        } else {
          // 直接作为当前通用签名回显
          this.signatureImage = signPic
          this.selectedSignature = signPic
        }
      } catch (e) {
        console.log('获取签名失败:', e)
        this.$message.error(e.message || '获取签名失败')
      } finally {
        this.signatureLoading = false
      }
    },

    // ===== 图片上传相关 =====
    async uploadRepairImage(param) {
      try {
        const res = await uploadOwnerImage({ file: param.file })
        const url = res && (res.url || (res.data && res.data.url))
        if (!url) {
          throw new Error('上传失败，未返回图片地址')
        }
        // 避免列表先展示本地图片再被整体替换导致闪烁：
        // 仅在原有 file 项上补充 url，而不是重新 push 新对象
        const file = param.file
        file.url = url
        const index = this.repairImages.findIndex(item => item.uid === file.uid)
        if (index > -1) {
          this.$set(this.repairImages, index, file)
        } else {
          this.repairImages.push(file)
        }
        param.onSuccess(res)
      } catch (e) {
        console.log('上传维修图片失败:', e)
        this.$message.error(e.message || '上传维修图片失败')
        param.onError(e)
      }
    },

    async uploadAcceptanceImage(param) {
      try {
        const res = await uploadOwnerImage({ file: param.file })
        const url = res && (res.url || (res.data && res.data.url))
        if (!url) {
          throw new Error('上传失败，未返回图片地址')
        }
        // 同样处理验收图片，更新现有 file 项，避免闪烁
        const file = param.file
        file.url = url
        const index = this.acceptanceImages.findIndex(item => item.uid === file.uid)
        if (index > -1) {
          this.$set(this.acceptanceImages, index, file)
        } else {
          this.acceptanceImages.push(file)
        }
        param.onSuccess(res)
      } catch (e) {
        console.log('上传验收图片失败:', e)
        this.$message.error(e.message || '上传验收图片失败')
        param.onError(e)
      }
    },

    // 是否“未处理”tab（仅未处理展示处理动作）
    isPendingTab() {
      return String(this.conditions && this.conditions.tabType) === '1'
    },

    async fetchData() {
      this.loading = true
      try {
        const params = {
          ...this.conditions,
          initiatorType: this.currentRoleRelCd || '1',
          handlerKey: this.currentRoleKey,
          page: this.page.current,
          row: this.page.size
        }

        // 如果是管理员（物业管理员权限组），强制设置参数
        if (this.isAdmin) {
          params.tabType = '3'
          params.initiatorType = '-1'
        } else {
          // 非管理员不能筛选状态，删除 status 参数
          delete params.status
        }

        // 过滤空值参数
        Object.keys(params).forEach(key => {
          if (params[key] === '') {
            delete params[key]
          }
        })

        const res = await listRepairPools(params)
        this.repairPools = res.data || []
        this.page.total = res.total || 0
      } catch (error) {
        console.log('Failed to fetch repair pools:', error)
      } finally {
        this.loading = false
      }
    },

    initOrderTypes() {
      this.orderTypes = [
        { orderType: '', name: this.$t('repairPoolManage.all') },
        { orderType: '0', name: this.$t('repairPoolManage.indoorRepair') },
        { orderType: '1', name: this.$t('repairPoolManage.publicAreaRepair') }
      ]
    },

    initTabTypes() {
      this.tabTypes = [
        { tabType: '1', name: this.$t('repairPoolManage.tabPending') },
        { tabType: '2', name: this.$t('repairPoolManage.tabProcessed') },
        { tabType: '0', name: this.$t('repairPoolManage.tabMyCreated') }
      ]
    },

    loadStates() {
      // 使用公共状态映射数据
      this.states = getRepairStatusOptions().map(item => ({
        name: item.label,
        statusCd: item.value
      }))
    },

    async loadRoles() {
      try {
        const savedRoleKey = localStorage.getItem('repairPoolManage_handlerKey') || ''
        const savedRoleName = localStorage.getItem('repairPoolManage_handlerName') || ''
        const staffId = getUserId()
        const res = await getStaffRoles({ staffId, page: 1, row: 100 })

        // 兼容不同返回结构：res.data / res.data.data
        const resData = res && res.data
        const rawList = Array.isArray(resData)
          ? resData
          : (resData && Array.isArray(resData.data) ? resData.data : [])

        this.roleList = rawList.map(r => {
          const keyVal =
            (r && r.pgId != null) ? r.pgId
              : (r && r.pId != null) ? r.pId
              : (r && r.roleId != null) ? r.roleId
                : (r && r.roleKey != null) ? r.roleKey
                  : (r && r.handlerKey != null) ? r.handlerKey
                    : (r && r.id != null) ? r.id
                      : ''

          const nameVal =
            (r && r.name != null) ? r.name
              : (r && r.roleName != null) ? r.roleName
              : (r && r.pgName != null) ? r.pgName
                : (r && r.role != null) ? r.role
                    : String(
                      (r && r.pgId != null) ? r.pgId
                        : (r && r.pId != null) ? r.pId
                        : (r && r.roleId != null) ? r.roleId
                          : ''
                    )

          const relCdVal = (r && r.relCd != null) ? String(r.relCd) : ''

          return {
            key: String(keyVal),
            name: nameVal,
            relCd: relCdVal
          }
        }).filter(r => r.key)

        let matched = null
        if (savedRoleKey) {
          matched = this.roleList.find(r => r.key === savedRoleKey)
        }
        if (!matched && savedRoleName) {
          matched = this.roleList.find(r => r.name === savedRoleName)
        }
        if (!matched) {
          matched = this.roleList[0]
        }

        this.currentRoleKey = matched ? matched.key : ''
        this.currentRoleName = matched ? matched.name : ''
        this.currentRoleRelCd = matched ? matched.relCd : ''
        if (this.currentRoleKey) {
          localStorage.setItem('repairPoolManage_handlerKey', this.currentRoleKey)
          localStorage.setItem('repairPoolManage_handlerName', this.currentRoleName)
          localStorage.setItem('repairPoolManage_relCd', this.currentRoleRelCd)
        } else {
          localStorage.removeItem('repairPoolManage_handlerKey')
          localStorage.removeItem('repairPoolManage_handlerName')
          localStorage.removeItem('repairPoolManage_relCd')
        }
        // 如果是管理员，强制设置 tabType 为 3
        if (this.isAdmin) {
          this.conditions.tabType = '3'
        }
      } catch (error) {
        console.log('Failed to load roles:', error)
        this.roleList = []
        this.currentRoleKey = ''
        this.currentRoleName = ''
      }
    },

    handleRoleChange(roleKey) {
      const target = this.roleList.find(r => r.key === String(roleKey))
      this.currentRoleKey = target ? target.key : String(roleKey)
      this.currentRoleName = target ? target.name : ''
      this.currentRoleRelCd = target ? target.relCd : ''
      localStorage.setItem('repairPoolManage_handlerKey', this.currentRoleKey)
      localStorage.setItem('repairPoolManage_handlerName', this.currentRoleName)
      localStorage.setItem('repairPoolManage_relCd', this.currentRoleRelCd)
      // 如果是管理员，强制设置 tabType 为 3，否则默认选中"未处理"
      if (this.isAdmin) {
        this.conditions.tabType = '3'
      } else {
        this.conditions.tabType = '1'
      }
      this.page.current = 1
      this.fetchData()
    },

    formatMaintenanceType(type) {
      const types = {
        '1001': this.$t('repairPoolManage.paidService'),
        '1002': this.$t('repairPoolManage.freeService'),
        '1003': this.$t('repairPoolManage.needMaterials'),
        '1004': this.$t('repairPoolManage.noMaterialsNeeded')
      }
      return types[type] || '--'
    },

    getOrderTypeText(orderType) {
      if (orderType === 0 || orderType === '0') {
        return this.$t('repairPoolManage.indoorRepair')
      } else if (orderType === 1 || orderType === '1') {
        return this.$t('repairPoolManage.publicAreaRepair')
      }
      return this.$t('repairPoolManage.all')
    },

    formatStatusName(status) {
      return getRepairStatusText(status)
    },

    getStatusTagType(status) {
      // 根据状态返回不同的 tag 类型（颜色）
      const statusStr = String(status)
      const tagTypeMap = {
        '0': 'info', // 空状态 - 灰色
        '1': 'warning', // 待派单 - 橙色
        '2': 'primary', // 勘察中 - 蓝色
        '3': 'primary', // 审批中 - 蓝色
        '4': 'primary', // 维修中 - 蓝色
        '5': 'warning', // 待验收 - 橙色
        '6': 'warning', // 待支付 - 橙色
        '7': 'success', // 已完成 - 绿色
        '9': 'danger', // 已取消 - 红色
        '10': 'primary', // 待指派供应商 - 蓝色
        '11': 'warning', // 待填写报价(审价) - 橙色
        '12': 'info' // 已转投诉单 - 灰色
      }
      return tagTypeMap[statusStr] || 'info'
    },

    getPayStatusText(payStatus) {
      const statusMap = {
        '0': '无',
        '1': '待支付',
        '2': '已支付'
      }
      return statusMap[String(payStatus)] || '-'
    },

    getLocationText(row) {
      // 如果是公区报修（orderType === 1），使用 addressDetail
      // 如果是室内报修（orderType === 0），使用 roomName
      if (row.orderType === 1 || row.orderType === '1') {
        return row.addressDetail || '-'
      } else if (row.orderType === 0 || row.orderType === '0') {
        return row.roomName || '-'
      }
      // 默认情况，优先显示 addressDetail，没有则显示 roomName
      return row.addressDetail || row.roomName || '-'
    },

    switchOrderType(item) {
      this.conditions.orderType = item.orderType
      this.page.current = 1
      this.fetchData()
    },

    queryRepairPool() {
      this.page.current = 1
      this.fetchData()
    },

    resetRepairPool() {
      // 仅重置右侧筛选，左侧报修类型保持当前选择
      this.conditions = {
        ...this.conditions,
        tabType: this.isAdmin ? '3' : '1',
        orderNo: '',
        initiatorName: '',
        status: this.isAdmin ? '' : undefined
      }
      // 如果不是管理员，删除 status 字段
      if (!this.isAdmin) {
        delete this.conditions.status
      }
      this.page.current = 1
      this.fetchData()
    },

    openEditModal(repair) {
      this.$refs.editOwnerRepair.open(repair)
    },

    openDispatchModal(repair) {
      this.$refs.dispatchRepair.open(repair)
    },

    async grabOrder(repair) {
      try {
        const data = {
          communityId: getCommunityId(),
          repairId: repair.id || repair.orderNo
        }

        await grabbingRepair(data)
        this.$message.success(this.$t('common.operationSuccess'))
        this.fetchData()
      } catch (error) {
        console.log('Grab order failed:', error)
      }
    },

    openEndModal(repair) {
      this.$refs.endRepair.open(repair)
    },

    async openDetail(repair) {
      const repairId = repair.id || repair.orderNo
      await this.loadRepairDetail(repairId)
    },
    
    // 打开预览弹窗
    // 打开预览弹窗
    async openPreviewModal(repair) {
      const repairId = repair.id || repair.orderNo
      
      try {
        // 先打开弹窗显示loading
        this.previewDialogVisible = true
        
        // 调用详情接口获取完整数据
        await this.loadPreviewRepairDetail(repairId)
      } catch (error) {
        console.log('加载预览数据失败:', error)
        this.$message.error('加载工单详情失败')
        this.previewDialogVisible = false
      }
    },
    
    // 加载预览用的工单详情 - 复用loadRepairDetail的逻辑
    async loadPreviewRepairDetail(repairId) {
      // 获取当前角色编码
      const savedRelCd = localStorage.getItem('repairPoolManage_relCd') || '1'
      const initiatorType = savedRelCd
      
      // 获取用户信息
      const userInfo = JSON.parse(localStorage.getItem('user') || '{}')
      const handlerKey = userInfo.pgId || userInfo.handlerKey || this.currentRoleKey
      
      // 调用接口获取工单详情
      const params = {
        communityId: getCommunityId(),
        initiatorType: initiatorType,
        userId: getUserId(),
        handlerKey: handlerKey,
        id: repairId,
        tabType: '1',
        page: 1,
        row: 1
      }
      
      const res = await listRepairPools(params)
      
      if (res.code === 0 && res.data && res.data.length > 0) {
        this.previewRepairDetail = res.data[0]
        // 调试：打印完整的工单数据
        console.log('预览工单数据:', this.previewRepairDetail)
      } else {
        throw new Error('未获取到工单详情')
      }
    },
    
    // 关闭预览弹窗
    closePreviewModal() {
      this.previewDialogVisible = false
      this.previewRepairDetail = null
    },
    
    // 加载工单详情
    async loadRepairDetail(repairId) {
      try {
        this.detailLoading = true
        this.detailDialogVisible = true

        // 每次加载新工单详情前，先重置与“处理详情”相关的回显数据，
        // 避免上一个工单的处理信息残留到当前工单
        this.displayMaterialList = []
        this.signatureList = []
        this.supplierInfoList = []
        this.auditControlInfo = {
          controlNum: '',
          controlRemark: ''
        }
        this.quoteInfo = {
          num: '',
          remark: ''
        }
        
        // 字典列表通常在进入页面时加载；这里兜底，避免为空时无法匹配动作
        if (!this.dictList || this.dictList.length === 0) {
          await this.loadDictList()
        }
        
        // 获取当前角色编码
        const savedRelCd = localStorage.getItem('repairPoolManage_relCd') || '1'
        const initiatorType = savedRelCd
        
        // 获取用户信息
        const userInfo = JSON.parse(localStorage.getItem('user') || '{}')
        const handlerKey = userInfo.pgId || userInfo.handlerKey || this.currentRoleKey
        
        // 调用接口获取工单详情，参考小程序的传参方式
        const params = {
          communityId: getCommunityId(),
          initiatorType: initiatorType,
          userId: getUserId(),
          handlerKey: handlerKey,
          id: repairId, // 传入工单ID
          tabType: '1', // 默认tabType
          page: 1,
          row: 1
        }
        
        const res = await listRepairPools(params)
        
        if (res.code === 0 && res.data && res.data.length > 0) {
          this.repairDetail = res.data[0]
          
          // 匹配操作动作
          if (this.repairDetail.semanticTag) {
            this.matchStatusCd(this.repairDetail.semanticTag)
          }

          // 默认保修期外（室内报修）
          this.warrantyType = false

          // 仅“未处理”且待派单时加载可指派人员列表（根据 professionId）
          if (this.isPendingTab() && this.matchedStatusCd === '1000') {
            this.loadStaffListByProfession()
          }
          
          // 解析材料清单（仅用于回显）
          if (this.repairDetail.materialList) {
            this.displayMaterialList = this.parseMaterialList(this.repairDetail.materialList)
          }
          
          // 解析签名列表（仅用于回显）
          if (this.repairDetail.signPics) {
            this.signatureList = this.parseSignatureList(this.repairDetail.signPics)
          }
          
          // 解析供应商信息列表（仅用于回显）
          if (this.repairDetail.supplierInfo) {
            this.supplierInfoList = this.parseSupplierInfoList(this.repairDetail.supplierInfo)
          }
          
          // 解析审计控价信息（仅用于回显）
          if (this.repairDetail.auditControl) {
            this.auditControlInfo = this.parseAuditControl(this.repairDetail.auditControl)
          }
          
          // 解析供应商报价信息（仅用于回显）
          if (this.repairDetail.quoteInfo) {
            this.quoteInfo = this.parseQuoteInfo(this.repairDetail.quoteInfo)
          }
        } else {
          this.$message.warning('未找到工单信息')
          this.repairDetail = null
        }
      } catch (error) {
        console.log('加载工单详情失败:', error)
        this.$message.error(error.message || '加载工单详情失败')
        this.repairDetail = null
      } finally {
        this.detailLoading = false
      }
    },

    // 根据详情里的 professionId 加载可指派人员（categoryId）
    async loadStaffListByProfession() {
      try {
        const professionId = this.repairDetail ? this.repairDetail.professionId : ''
        if (!professionId) {
          this.staffList = []
          return
        }

        const res = await getUserByCategoryId({ categoryId: professionId })
        // 兼容不同返回结构：res.data / res.users
        const list = Array.isArray(res.data) ? res.data : (Array.isArray(res.users) ? res.users : [])
        // 归一化，兼容 user_id / userId / id
        this.staffList = list.map(u => ({
          ...u,
          id: (u && (u.user_id != null)) ? u.user_id : (u && (u.userId != null)) ? u.userId : (u && (u.id != null)) ? u.id : '',
          name: (u && (u.name != null)) ? u.name : (u && (u.userName != null)) ? u.userName : (u && (u.user_name != null)) ? u.user_name : '',
          tel: (u && (u.tel != null)) ? u.tel : (u && (u.phone != null)) ? u.phone : (u && (u.mobile != null)) ? u.mobile : (u && (u.user_tel != null)) ? u.user_tel : ''
        })).filter(u => u.id)
      } catch (e) {
        console.log('加载指派人员列表失败:', e)
        this.staffList = []
      }
    },
    
    // 加载字典列表（操作动作）
    async loadDictList() {
      if (this.dictLoading) return
      if (this.dictList && this.dictList.length > 0) return
      try {
        this.dictLoading = true
        const res = await getDictList({ page: 1, row: 100, specId: 8 })
        this.dictList = res.data || []
      } catch (error) {
        console.log('加载字典列表失败:', error)
        this.dictList = []
      } finally {
        this.dictLoading = false
      }
    },
    
    // 匹配状态码（根据semanticTag匹配statusCd）
    matchStatusCd(semanticTag) {
      if (!semanticTag || !this.dictList.length) {
        return ''
      }
      
      const matched = this.dictList.find(item => item.name === semanticTag)
      if (matched) {
        this.matchedStatusCd = matched.statusCd
        console.log(`匹配成功: semanticTag=${semanticTag}, statusCd=${matched.statusCd}`)
        
        // 根据statusCd设置当前动作
        this.setCurrentActionByStatusCd(matched.statusCd)
        
        return matched.statusCd
      }
      
      console.warn(`未找到匹配的字典项: semanticTag=${semanticTag}`)
      return ''
    },
    
    // 根据statusCd设置当前动作
    setCurrentActionByStatusCd(statusCd) {
      const dictItem = this.dictList.find(item => item.statusCd === statusCd)
      
      if (dictItem) {
        this.currentAction = dictItem.name
        console.log(`设置动作: statusCd=${statusCd}, currentAction=${dictItem.name}`)
      } else {
        this.currentAction = (this.repairDetail && this.repairDetail.semanticTag) ? this.repairDetail.semanticTag : ''
        console.warn(`字典中未找到statusCd=${statusCd}的配置，使用semanticTag=${this.currentAction}`)
      }
    },
    
    // 判断是否显示指定动作
    isAction(statusCd) {
      return this.matchedStatusCd === statusCd
    },
    
    // 获取操作标题
    getActionTitle() {
      const titleMap = {
        '1000': '指派维修人员',
        '1001': '勘察情况',
        '1002': '填写报价(审价)',
        '1003': '维修信息',
        '1004': '审核操作',
        '1005': '验收操作',
        '1006': '指派供应商',
        '1007': '确认维修责任方',
        '9999': '工单结束'
      }
      return titleMap[this.matchedStatusCd] || this.currentAction || ''
    },
    
    // 判断是否是公区报修
    isPublicAreaRepair() {
      return this.repairDetail && (this.repairDetail.orderType === 1 || this.repairDetail.orderType === '1')
    },
    
    // 判断是否显示操作区域
    shouldShowActionSection() {
      // 待派单状态：公区报修直接显示，业主报修需要选择保修期外才显示
      if (this.isAction('1000')) {
        return this.isPublicAreaRepair() || this.warrantyType === false
      }
      // 其他状态：有matchedStatusCd就显示
      return !!this.matchedStatusCd && !this.isAction('9999')
    },
    
    // 判断是否显示提交按钮
    showSubmitButton() {
      // 审核、验收、工单结束不显示提交按钮
      return this.matchedStatusCd && !['1004', '1005', '9999'].includes(this.matchedStatusCd)
    },
    
    // 解析材料列表
    parseMaterialList(materialListData) {
      if (!materialListData) return []
      try {
        if (Array.isArray(materialListData)) {
          return materialListData
        }
        if (typeof materialListData === 'string') {
          const parsed = JSON.parse(materialListData)
          if (Array.isArray(parsed)) {
            return parsed
          } else if (typeof parsed === 'object' && parsed !== null) {
            return [parsed]
          }
        }
        if (typeof materialListData === 'object' && materialListData !== null) {
          return [materialListData]
        }
        return []
      } catch (err) {
        console.log('解析材料列表失败:', err)
        return []
      }
    },
    
    // 解析签名列表
    parseSignatureList(signPicsData) {
      if (!signPicsData) return []
      try {
        let dataArray = []
        if (typeof signPicsData === 'string') {
          let cleanedData = signPicsData.replace(/,(\s*[}\]])/g, '$1')
          try {
            signPicsData = JSON.parse(cleanedData)
          } catch (parseErr) {
            // 签名数据后端格式可能不规范，这里静默降级，不影响页面其他功能
            console.warn('签名数据解析失败，已忽略该字段:', parseErr)
            return []
          }
        }
        if (Array.isArray(signPicsData)) {
          dataArray = signPicsData
        } else if (typeof signPicsData === 'object' && signPicsData !== null) {
          dataArray = [signPicsData]
        } else {
          return []
        }
        const result = []
        for (let item of dataArray) {
          if (typeof item === 'string') {
            try {
              let cleanedItem = item.replace(/,(\s*[}\]])/g, '$1')
              const parsed = JSON.parse(cleanedItem)
              if (parsed && typeof parsed === 'object') {
                result.push(parsed)
              }
            } catch (e) {
              // 单条签名解析失败同样忽略
              console.warn('解析单条签名信息失败，已跳过:', item, e)
            }
          } else if (typeof item === 'object' && item !== null) {
            result.push(item)
          }
        }
        return result
      } catch (err) {
        console.warn('解析签名列表失败，已忽略签名信息:', err)
        return []
      }
    },
    
    // 解析供应商信息列表
    parseSupplierInfoList(supplierInfoData) {
      if (!supplierInfoData) return []
      try {
        let dataArray = []
        if (typeof supplierInfoData === 'string') {
          supplierInfoData = JSON.parse(supplierInfoData)
        }
        if (Array.isArray(supplierInfoData)) {
          dataArray = supplierInfoData
        } else if (typeof supplierInfoData === 'object' && supplierInfoData !== null) {
          dataArray = [supplierInfoData]
        } else {
          return []
        }
        const result = []
        for (let item of dataArray) {
          if (typeof item === 'string') {
            try {
              const parsed = JSON.parse(item)
              if (parsed && typeof parsed === 'object') {
                result.push(parsed)
              }
            } catch (e) {
              console.warn('解析供应商信息项失败:', item, e)
            }
          } else if (typeof item === 'object' && item !== null) {
            result.push(item)
          }
        }
        return result
      } catch (err) {
        console.log('解析供应商信息列表失败:', err)
        return []
      }
    },
    
    // 解析审计控价信息
    parseAuditControl(auditControlData) {
      const defaultResult = {
        controlNum: '',
        controlRemark: ''
      }
      if (!auditControlData) {
        return defaultResult
      }
      try {
        let parsed = auditControlData
        if (typeof auditControlData === 'string') {
          parsed = JSON.parse(auditControlData)
        }
        if (typeof parsed === 'object' && parsed !== null) {
          return {
            controlNum: parsed.controlNum || '',
            controlRemark: parsed.controlRemark || parsed.controlRemake || ''
          }
        }
        return defaultResult
      } catch (err) {
        console.log('解析审计控价信息失败:', err)
        return defaultResult
      }
    },
    
    // 解析供应商报价信息
    parseQuoteInfo(quoteInfoData) {
      const defaultResult = {
        num: '',
        remark: ''
      }
      if (!quoteInfoData) {
        return defaultResult
      }
      try {
        let parsed = quoteInfoData
        if (typeof quoteInfoData === 'string') {
          parsed = JSON.parse(quoteInfoData)
        }
        if (typeof parsed === 'object' && parsed !== null) {
          return {
            num: parsed.num || '',
            remark: parsed.remark || ''
          }
        }
        return defaultResult
      } catch (err) {
        console.log('解析供应商报价信息失败:', err)
        return defaultResult
      }
    },
    
    // 获取维修过程图片列表
    getProcessImageList(repairProcessImgs) {
      if (!repairProcessImgs) return []
      try {
        const data = typeof repairProcessImgs === 'string' 
          ? JSON.parse(repairProcessImgs) 
          : repairProcessImgs
        if (data && data.images) {
          if (typeof data.images === 'string') {
            return data.images.split(',').filter(img => img.trim())
          }
          if (Array.isArray(data.images)) {
            return data.images.filter(img => img && img.trim())
          }
        }
        return []
      } catch (err) {
        console.log('解析维修图片失败:', err)
        return []
      }
    },
    
    // 获取验收图片列表
    getAcceptanceImageList(repairAfterImgs) {
      if (!repairAfterImgs) return []
      try {
        let data
        if (typeof repairAfterImgs === 'string') {
          data = JSON.parse(repairAfterImgs)
        } else {
          data = repairAfterImgs
        }
        if (data && data.images) {
          if (typeof data.images === 'string') {
            return data.images.split(',').filter(img => img.trim())
          }
          if (Array.isArray(data.images)) {
            return data.images.filter(img => img && img.trim())
          }
        }
        return []
      } catch (err) {
        console.log('解析验收图片失败:', err)
        return []
      }
    },
    
    // 获取评价图片列表
    getRateImageList(rateImgs) {
      if (!rateImgs) return []
      try {
        if (typeof rateImgs === 'string') {
          try {
            const parsed = JSON.parse(rateImgs)
            if (Array.isArray(parsed)) {
              return parsed.filter(img => img && img.trim())
            }
          } catch (e) {
            return rateImgs.split(',').filter(img => img.trim())
          }
        }
        if (Array.isArray(rateImgs)) {
          return rateImgs.filter(img => img && img.trim())
        }
        return []
      } catch (err) {
        console.log('解析评价图片失败:', err)
        return []
      }
    },
    
    // 获取报修图片列表
    getImageList(problemImgs) {
      if (!problemImgs) return []
      if (typeof problemImgs === 'string') {
        return problemImgs.split(',').filter(img => img.trim())
      }
      if (Array.isArray(problemImgs)) {
        return problemImgs.filter(img => img && img.trim())
      }
      return []
    },
    
    // 获取位置文本（详情弹窗中使用，参考列表的逻辑）
    getDetailLocationText() {
      if (!this.repairDetail) return '-'
      // 如果是公区报修（orderType === 1），使用 addressDetail
      // 如果是室内报修（orderType === 0），使用 roomName
      if (this.repairDetail.orderType === 1 || this.repairDetail.orderType === '1') {
        return this.repairDetail.addressDetail || '-'
      } else if (this.repairDetail.orderType === 0 || this.repairDetail.orderType === '0') {
        return this.repairDetail.roomName || '-'
      }
      // 默认情况，优先显示 addressDetail，没有则显示 roomName
      return this.repairDetail.addressDetail || this.repairDetail.roomName || '-'
    },
    
    // 计算材料总价
    calculateMaterialTotal(material) {
      const price = parseFloat(material.price) || 0
      const num = parseFloat(material.num) || 0
      return (price * num).toFixed(2)
    },
    
    // 获取保修期文本
    getWarrantyTypeText(warrantyType) {
      if (warrantyType === 1 || warrantyType === '1') {
        return '保修期内'
      } else if (warrantyType === 2 || warrantyType === '2') {
        return '已过保'
      }
      return '-'
    },

    openDeleteModal(repairId) {
      this.$refs.deleteOwnerRepair.open({repairId})
    },

    handleSizeChange(size) {
      this.page.size = size
      this.fetchData()
    },

    handleCurrentChange(current) {
      this.page.current = current
      this.fetchData()
    },

    // 计算材料总价（用于操作区域显示）
    calculateMaterialTotalPrice() {
      if (!this.confirmedMaterials || this.confirmedMaterials.length === 0) {
        return '0.00'
      }
      const total = this.confirmedMaterials.reduce((sum, material) => {
        const price = parseFloat(material.price) || 0
        const num = parseFloat(material.num) || 0
        return sum + (price * num)
      }, 0)
      return total.toFixed(2)
    },
    
    // 处理提交
    async handleSubmit() {
      try {
        let actionData = ''
        
        // 指派人员 (statusCd: 1000)
        if (this.isAction('1000')) {
          // 先按小程序逻辑保存/更新工单（保修期选择等）
          const isPublic = this.isPublicAreaRepair()

          // 室内报修必须先选择保修期
          if (!isPublic && this.warrantyType === null) {
            this.$message.warning('请选择保修期类型')
            return
          }

          const userId = getUserId()
          const userInfo = JSON.parse(localStorage.getItem('user') || '{}')
          const handlerKey = userInfo.pgId || userInfo.handlerKey || this.currentRoleKey

          // warrantyType: 公区报修传0；室内：保修期内=1，保修期外=2
          let warrantyTypeCd = 0
          if (!isPublic) {
            warrantyTypeCd = this.warrantyType ? 1 : 2
          }

          await saveOrUpdateRepairOrder({
            id: this.repairDetail.id || this.repairDetail.orderNo,
            warrantyType: warrantyTypeCd,
            templateId: this.repairDetail.templateId,
            dispatcherId: userId,
            handlerKey: handlerKey
          })

          // 保修期内：提交后不走处理工单接口（小程序一致）
          if (!isPublic && this.warrantyType === true) {
            this.$message.success('提交成功')
            this.detailDialogVisible = false
            this.fetchData()
            return
          }

          // 公区报修 or 室内保修期外：需要指派人员并走流程
          if (!this.selectedStaffId) {
            this.$message.warning('请选择指派人员')
            return
          }
          actionData = JSON.stringify({ userId: this.selectedStaffId })
          await this.handleExecuteFlow(true, actionData)
          return
        }
        
        // 勘察 (statusCd: 1001)
        if (this.isAction('1001')) {
          if (!this.canRepair) {
            this.$message.warning('请选择是否能修')
            return
          }
          if (this.canRepair === '1' && this.confirmedMaterials.length === 0) {
            this.$message.warning('请选择所需材料')
            return
          }
          let materialsData = ''
          if (this.canRepair === '1' && this.confirmedMaterials.length > 0) {
            materialsData = JSON.stringify(this.confirmedMaterials)
          }
          const canRepairBool = this.canRepair === '1'
          await this.handleExecuteFlow(canRepairBool, materialsData)
          return
        }
        
        // 填写报价(审价)/控价 (statusCd: 1002)
        if (this.isAction('1002')) {
          if (this.repairDetail.quoteInfo) {
            // 填写控价
            if (!this.controlAmount) {
              this.$message.warning('请输入控价金额')
              return
            }
            actionData = JSON.stringify({
              controlNum: this.controlAmount,
              controlRemark: this.controlRemark || ''
            })
          } else {
            // 填写报价(审价)
            if (!this.quoteAmount) {
              this.$message.warning('请输入报价金额')
              return
            }
            actionData = JSON.stringify({
              num: this.quoteAmount,
              remark: this.quoteRemark || ''
            })
          }
        }
        
        // 维修 (statusCd: 1003)
        if (this.isAction('1003')) {
          if (this.repairImages.length === 0) {
            this.$message.warning('请上传维修图片')
            return
          }
          actionData = JSON.stringify({
            images: this.repairImages.map(img => img.url || img).join(',')
          })
        }
        
        // 指派供应商 (statusCd: 1006)
        if (this.isAction('1006')) {
          if (this.selectedSuppliers.length === 0) {
            this.$message.warning('请选择供应商')
            return
          }
          const suppliersData = this.selectedSuppliers.map(supplier => ({
            name: supplier.name || supplier.supplierName,
            rsId: supplier.rsId,
            tel: supplier.tel,
            contactName: supplier.contactName
          }))
          actionData = JSON.stringify(suppliersData)
        }
        
        // 确认维修责任方 (statusCd: 1007)
        if (this.isAction('1007')) {
          if (!this.responsibilityType) {
            this.$message.warning('请选择维修责任方')
            return
          }
          if (!this.selectedSignature) {
            this.$message.warning('请选择您的签名')
            return
          }
          const userInfo = JSON.parse(localStorage.getItem('user') || '{}')
          const roleName = this.currentRoleName || userInfo.roleName || userInfo.name || ''
          actionData = JSON.stringify({
            roleName: roleName,
            img: this.selectedSignature
          })
          const isPropertyResponsible = this.responsibilityType === '1'
          await this.handleExecuteFlow(isPropertyResponsible, actionData)
          return
        }
        
        // 调用处理工单流程
        await this.handleExecuteFlow(true, actionData)
      } catch (error) {
        console.log('提交失败:', error)
      }
    },
    
    // 处理工单流程
    async handleExecuteFlow(isPass, actionData = '', repairAfterImgs = '') {
      try {
        if (!this.repairDetail || !this.repairDetail.id) {
          this.$message.warning('缺少工单ID')
          return
        }
        
        if (!this.repairDetail.currNodeId) {
          this.$message.warning('缺少节点信息')
          return
        }
        
        this.submitLoading = true
        
        const userInfo = JSON.parse(localStorage.getItem('user') || '{}')
        const handlerKey = userInfo.pgId || userInfo.handlerKey || this.currentRoleKey
        
        const params = {
          orderId: this.repairDetail.id || this.repairDetail.orderNo,
          currentNodeId: this.repairDetail.currNodeId,
          isPass: isPass,
          userId: getUserId(),
          handlerKey: handlerKey,
          actionData: actionData
        }
        
        // 如果有验收图片，添加到参数中
        if (repairAfterImgs) {
          params.repairAfterImgs = repairAfterImgs
        }
        
        await executeFlowProcess(params)
        
        this.$message.success('操作成功')
        this.detailDialogVisible = false
        this.fetchData() // 刷新列表
      } catch (error) {
        console.log('处理工单失败:', error)
        this.$message.error(error.message || '操作失败')
      } finally {
        this.submitLoading = false
      }
    },
    
    // 同意（审核/验收）
    async handleApprove() {
      try {
        // 审核环节需要根据isSign判断是否需要签名
        if (this.isAction('1004')) {
          if ((this.repairDetail.isSign === 1 || this.repairDetail.isSign === '1') && !this.selectedSignature) {
            this.$message.warning('请选择您的签名')
            return
          }
          
          await this.$confirm(`确认同意工单 ${this.repairDetail.orderNo}？`, '确认', {
            type: 'warning'
          })
          
          let actionData = ''
          if ((this.repairDetail.isSign === 1 || this.repairDetail.isSign === '1') && this.selectedSignature) {
            const userInfo = JSON.parse(localStorage.getItem('user') || '{}')
            const roleName = this.currentRoleName || userInfo.roleName || userInfo.name || ''
            const roleId = this.currentRoleRelCd || ''
            actionData = JSON.stringify({
              roleName: roleName,
              img: this.selectedSignature,
              roleId: roleId
            })
          }
          await this.handleExecuteFlow(true, actionData)
          return
        }
        
        // 验收环节
        if (this.isAction('1005')) {
          if ((this.repairDetail.isSign === 1 || this.repairDetail.isSign === '1') && !this.selectedSignature) {
            this.$message.warning('请选择您的签名')
            return
          }
          
          await this.$confirm(`确认同意工单 ${this.repairDetail.orderNo}？`, '确认', {
            type: 'warning'
          })
          
          let actionData = ''
          if ((this.repairDetail.isSign === 1 || this.repairDetail.isSign === '1') && this.selectedSignature) {
            const userInfo = JSON.parse(localStorage.getItem('user') || '{}')
            const roleName = this.currentRoleName || userInfo.roleName || userInfo.name || ''
            actionData = JSON.stringify({
              roleName: roleName,
              img: this.selectedSignature
            })
          }
          
          let repairAfterImgs = ''
          if (this.acceptanceImages.length > 0) {
            repairAfterImgs = JSON.stringify({
              images: this.acceptanceImages.map(img => img.url || img).join(',')
            })
          }
          
          await this.handleExecuteFlow(true, actionData, repairAfterImgs)
          return
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.log('操作失败:', error)
        }
      }
    },

    // 保修期类型选择（参考小程序）
    onWarrantyTypeChange(value) {
      // element-radio-group 绑定的是 boolean
      this.warrantyType = value
      // 如果切换到保修期内，清空已选择的人员
      if (this.warrantyType === true) {
        this.selectedStaff = ''
        this.selectedStaffId = ''
        this.staffList = []
      } else if (this.warrantyType === false) {
        // 保修期外：需要指派人员，重新拉取人员列表
        if (this.isAction && this.isAction('1000')) {
          this.loadStaffListByProfession()
        }
      }
    },
    
    // 处理图片预览
    handlePictureCardPreview(file) {
      if (file && file.url) {
        window.open(file.url, '_blank')
      }
    },
    
    // 维修图片移除
    handleRepairRemove(file) {
      if (!file || !file.url) return
      const index = this.repairImages.findIndex(img => img.url === file.url)
      if (index > -1) {
        this.repairImages.splice(index, 1)
      }
    },

    // 验收图片移除
    handleAcceptanceRemove(file) {
      if (!file || !file.url) return
      const index = this.acceptanceImages.findIndex(img => img.url === file.url)
      if (index > -1) {
        this.acceptanceImages.splice(index, 1)
      }
    },
    
    // 驳回
    async handleReject() {
      if (!this.rejectReason || !this.rejectReason.trim()) {
        this.$message.warning('请输入驳回理由')
        return
      }
      
      this.showRejectModal = false
      const actionData = this.rejectReason.trim()
      await this.handleExecuteFlow(false, actionData)
    },

    // 转投诉单（从列表操作）
    async handleChangeToComplaint(repair) {
      try {
        // 确认操作
        await this.$confirm('确定要将此工单转为投诉单吗？', '提示', {
          type: 'warning'
        })

        // 获取当前角色信息
        const userInfo = JSON.parse(localStorage.getItem('user') || '{}')
        const handlerKey = userInfo.pgId || userInfo.handlerKey || this.currentRoleKey
        const currentUserId = getUserId()

        // 判断reportType：如果initiatorId等于当前登录userId则为1（自报），否则为2（代报）
        const reportType = (repair.initiatorId === currentUserId) ? 1 : 2

        // 从工单详情中提取数据
        const complaintData = {
          complaintNo: repair.orderNo || '', // 工单号
          repairId: repair.id || repair.orderNo, // 关联的报修单ID
          relatedRepairId: repair.id || repair.orderNo,
          ownerId: repair.initiatorId || '',
          ownerName: repair.initiatorName || '',
          contactPhone: repair.initiatorPhone || '',
          content: repair.problemDesc || '',
          communityId: getCommunityId(),
          communityName: repair.communityName || '',
          problemImgs: repair.problemImgs || '',
          status: 0, // 默认状态为待处理
          handlerKey: handlerKey,
          reportType: reportType, // 代报或自报：1自报，2代报
          staffId: reportType === 1 ? '' : currentUserId, // reportType为1时传空，为2时传当前用户ID
          staffName: reportType === 1 ? '' : (userInfo.userName || userInfo.name || '') // reportType为1时传空，为2时传当前用户名
        }

        // 调用转投诉接口
        await repairChangeComplaint(complaintData)

        this.$message.success('转投诉成功')
        this.fetchData() // 刷新列表
      } catch (error) {
        if (error !== 'cancel') {
          console.log('转投诉失败:', error)
          this.$message.error(error.message || '转投诉失败')
        }
      }
    },

    // 转投诉单（从详情弹窗）
    async handleChangeToComplaintFromDetail() {
      if (!this.repairDetail) {
        this.$message.warning('工单信息不存在')
        return
      }

      try {
        // 确认操作
        await this.$confirm('确定要将此工单转为投诉单吗？', '提示', {
          type: 'warning'
        })

        // 获取当前角色信息
        const userInfo = JSON.parse(localStorage.getItem('user') || '{}')
        const handlerKey = userInfo.pgId || userInfo.handlerKey || this.currentRoleKey
        const currentUserId = getUserId()

        // 判断reportType：如果initiatorId等于当前登录userId则为1（自报），否则为2（代报）
        const reportType = (this.repairDetail.initiatorId === currentUserId) ? 1 : 2

        // 从工单详情中提取数据
        const complaintData = {
          complaintNo: this.repairDetail.orderNo || '', // 工单号
          repairId: this.repairDetail.id || this.repairDetail.orderNo, // 关联的报修单ID
          relatedRepairId: this.repairDetail.id || this.repairDetail.orderNo,
          ownerId: this.repairDetail.initiatorId || '',
          ownerName: this.repairDetail.initiatorName || '',
          contactPhone: this.repairDetail.initiatorPhone || '',
          content: this.repairDetail.problemDesc || '',
          communityId: getCommunityId(),
          communityName: this.repairDetail.communityName || '',
          problemImgs: this.repairDetail.problemImgs || '',
          status: 0, // 默认状态为待处理
          handlerKey: handlerKey,
          reportType: reportType, // 代报或自报：1自报，2代报
          staffId: reportType === 1 ? '' : currentUserId, // reportType为1时传空，为2时传当前用户ID
          staffName: reportType === 1 ? '' : (userInfo.userName || userInfo.name || '') // reportType为1时传空，为2时传当前用户名
        }

        // 调用转投诉接口
        await repairChangeComplaint(complaintData)

        this.$message.success('转投诉成功')
        this.detailDialogVisible = false
        this.fetchData() // 刷新列表
      } catch (error) {
        if (error !== 'cancel') {
          console.log('转投诉失败:', error)
          this.$message.error(error.message || '转投诉失败')
        }
      }
    },

    // 修改工单
    handleEditRepair() {
      if (!this.repairDetail || !this.repairDetail.id) {
        this.$message.warning('工单信息不存在')
        return
      }
      
      // 打开修改工单弹窗
      this.editingRepairId = this.repairDetail.id
      this.editRepairDialogVisible = true
    },
    
    // 修改工单成功回调
    handleEditSuccess() {
      this.editRepairDialogVisible = false
      this.detailDialogVisible = false
      this.$message.success('修改成功')
      this.fetchData() // 刷新列表
    },
    
    // 取消修改工单
    handleEditCancel() {
      this.editRepairDialogVisible = false
    },
    
    // 修改工单弹窗关闭回调
    handleEditDialogClose() {
      this.editingRepairId = null
    }

  }
}
</script>

<style scoped>
/* el-descriptions 单元格最小宽度 */
::v-deep .el-descriptions.is-bordered .el-descriptions-item__cell {
  min-width: 100px;
}

.status-list {
  display: flex;
  flex-direction: column;
}

.status-item {
  padding: 12px;
  margin-bottom: 8px;
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  transition: all 0.3s;
}

.status-item:hover {
  background-color: #f5f7fa;
}

.status-active {
  background-color: #409eff;
  color: white;
}

/* 左侧独立“员工报修”按钮样式 */
.staff-repair-btn {
  margin-top: 16px;
  width: 100%;
}

.box-card {
  margin-bottom: 20px;
}

.header-with-role {
  display: flex;
  align-items: center;
  gap: 15px;
}

.role-switch {
  display: flex;
  align-items: center;
}

.role-switch-btn {
  font-weight: 600;
  letter-spacing: 0.2px;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.25);
}

.role-switch-icon {
  margin-right: 6px;
}

/* 工单详情样式 */
.repair-detail-content {
  padding: 0;
  /* 覆盖弹窗 body 的居中对齐，整体内容左对齐 */
  text-align: left;
}

.detail-section {
  margin-bottom: 20px;
}

.section-title {
  text-align: left;
  /* 保证占满整行，避免被居中 */
  display: inline-block;
  font-weight: 600;
  font-size: 16px;
  color: #333;
  padding-left: 10px;
  border-left: 4px solid #409EFF;
}

/* 覆盖默认卡片头部居中，整体左对齐 */
.detail-section ::v-deep .el-card__header {
  text-align: left;
  justify-content: flex-start;
}

.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}

.detail-image {
  width: 120px;
  height: 120px;
  border-radius: 4px;
  cursor: pointer;
}

.empty-detail {
  padding: 40px 0;
  text-align: center;
}

.material-summary {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #E8E8E8;
}

.summary-label {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.summary-price {
  font-size: 18px;
  color: #FF6B35;
  font-weight: 600;
}

.quote-price,
.control-price {
  font-size: 18px;
  color: #FF6B35;
  font-weight: 600;
  display: inline-block;
  min-width: 120px;
}

.remark-content {
  padding: 10px;
  background: #F8F9FA;
  border: 1px solid #E0E0E0;
  border-radius: 4px;
  line-height: 1.6;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.rate-content {
  padding: 10px 0;
}

.rate-text {
  font-size: 14px;
  color: #333;
  line-height: 1.6;
  margin-bottom: 10px;
  word-break: break-all;
}

.supplier-info-table {
  margin-top: 10px;
}

.signature-record-item {
  display: inline-block;
  vertical-align: top;
  margin: 0 16px 16px 0;
  padding: 10px;
  border: 1px solid #E8E8E8;
  border-radius: 4px;
  width: 300px;
}

.signature-record-item:last-child {
  margin-bottom: 0;
}

.signature-role-name {
  font-size: 14px;
  color: #333;
  font-weight: 600;
  margin-bottom: 10px;
  padding-left: 10px;
  border-left: 4px solid #409EFF;
}

.signature-image-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;

  padding: 6px;
  border: 1px solid #E8E8E8;
  border-radius: 4px;
  background: #F8F9FA;
}

.signature-image {
  width: 100%;
  height: 100%;
}

/* 操作区域样式 */
.action-section {
  margin-top: 20px;
}

.action-card {
  border: 1px solid #E8E8E8;
}

.action-title {
  /* 与上方“处理详情”等标题保持完全一致的样式 */
  text-align: left;
  display: inline-block;
  font-weight: 600;
  font-size: 16px;
  color: #333;
  padding-left: 10px;
  border-left: 4px solid #409EFF;
  margin-bottom: 16px;
}

.sub-section {
  margin-bottom: 20px;
}

.sub-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.action-content {
  padding: 10px 0;
  text-align: left;
}

/* 操作区内容左对齐（radio / form item） */
.action-card ::v-deep .el-form-item__content {
  text-align: left;
}

.action-card ::v-deep .el-radio-group {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
}

/* 指派人员单选列表 */
.staff-radio-group {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px 24px;
}

.staff-radio-item {
  margin-right: 0 !important;
}

/* 去掉el-form-item左侧label占位 */
.no-item-label ::v-deep .el-form-item__label {
  display: none;
}
.no-item-label ::v-deep .el-form-item__content {
  margin-left: 0 !important;
}

.action-buttons {
  display: flex;
  gap: 10px;
  justify-content: flex-start;
  margin-top: 20px;
}

.submit-section {
  margin-top: 20px;
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #E8E8E8;
}

/* 让“立即提交 / 转投诉单”并排展示 */
.submit-section-inline {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
}

.confirmed-materials {
  margin-top: 10px;
  padding: 10px;
  background: #F8F9FA;
  border-radius: 4px;
}

.material-item {
  padding: 5px 0;
  font-size: 14px;
  color: #666;
}

.material-total {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #E8E8E8;
  text-align: right;
  font-weight: 600;
  color: #FF6B35;
}

.selected-suppliers {
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.selected-supplier-item {
  padding: 12px;
  background: #F8F9FA;
  border: 1px solid #E8E8E8;
  border-radius: 4px;
}

.selected-supplier-row {
  font-size: 14px;
  color: #666;
  line-height: 22px;
}

/* 勘察-材料选择列表 */
.material-toolbar {
  margin-bottom: 10px;
}

.material-search-input {
  max-width: 280px;
}

.material-table-wrapper {
  max-height: 400px;
  overflow-y: auto;
  margin-top: 4px;
}

.material-table {
  width: 100%;
}

.material-quantity-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.material-quantity-cell ::v-deep .el-input-number {
  width: 110px;
}

.material-quantity-cell ::v-deep .el-input-number__decrease,
.material-quantity-cell ::v-deep .el-input-number__increase {
  width: 28px;
}

.material-quantity-cell ::v-deep .el-input__inner {
  padding-left: 0;
  padding-right: 0;
  text-align: center;
}

.material-unit {
  margin-left: 4px;
  font-size: 12px;
  color: #666;
}

.material-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}

/* 指派供应商：列表 & 搜索样式（参考材料选择区域） */
.supplier-section {
  margin-top: 4px;
}

.supplier-toolbar {
  margin-bottom: 10px;
}

.supplier-search-input {
  max-width: 280px;
}

.supplier-table {
  margin-top: 4px;
}

.supplier-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}

.selected-suppliers-text {
  font-size: 14px;
  color: #333;
}

/* 确认维修责任方区域高度 & 对齐 */
.responsibility-item {
  margin-bottom: 0;
}

.responsibility-item ::v-deep .el-form-item__label,
.responsibility-item ::v-deep .el-form-item__content {
  line-height: 40px;
  min-height: 40px;
}

.responsibility-item ::v-deep .el-radio-group {
  display: flex;
  align-items: center;
}

.responsibility-item ::v-deep .el-radio {
  line-height: 40px;
}

/* 签名弹窗样式 */
.signature-dialog-loading,
.signature-dialog-empty {
  text-align: center;
  padding: 40px 0;
  color: #999;
}

.signature-dialog-preview {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
}

/* 确认维修责任方 - 签名回显与按钮布局 */
.signature-select-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* 左对齐 */
  margin-top: 12px;        /* 与上面内容拉开一点距离 */
}

.signature-preview-image {
  width: 260px;
  max-height: 140px;
  border: 1px solid #E0E0E0;
  margin-bottom: 10px;
}

.signature-select-button {
  margin-top: 8px;
}

/* 查询条件 - 状态单选组 */
.status-radio-col {
  display: flex;
  align-items: center;
}

.status-radio-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.status-radio-item {
  margin-right: 24px !important;
}

/* 查询条件 - 顶部未处理/已处理/我发起的 单选组 */
.tabtype-radio-col {
  display: flex;
  align-items: center;
  /* 让单选组与右侧输入框垂直对齐 */
  padding-top: 14px;
}

.tabtype-radio-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.tabtype-radio-item {
  margin-right: 24px !important;
}

@media (max-width: 768px) {
  .tabtype-radio-col {
    margin-bottom: 10px;
  }
}

/* 查询条件行间距 */
.tabtype-row {
  margin-bottom: 10px;
}

.condition-row {
  margin-top: 4px;
}

@media (max-width: 768px) {
  .status-radio-col {
    margin-top: 10px;
  }
}

/* 勘察：是否能修 样式优化 */
.can-repair-item ::v-deep .el-form-item__label {
  display: flex;
  align-items: center;
  /* 去掉多余缩进，让标签靠左对齐 */
  width: auto !important;
  margin-right: 16px;
  height: 40px;
  line-height: 40px;
}

.can-repair-item ::v-deep .el-form-item__content {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  /* 取消默认label宽度带来的左侧缩进 */
  margin-left: 0 !important;
  height: 40px;
}

.can-repair-radios {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 24px;
}

.can-repair-radios ::v-deep .el-radio {
  margin-right: 0 !important;
}

.can-repair-radios ::v-deep .el-radio__label {
  font-size: 14px;
}

/* 操作栏按钮禁用过渡动效 */
::v-deep .el-table .el-table__body-wrapper .el-table__body .el-table__row .el-table__cell:last-child .el-button {
  transition: none !important;
  animation: none !important;
}

::v-deep .el-table .el-table__body-wrapper .el-table__body .el-table__row .el-table__cell:last-child .el-button:hover {
  transition: none !important;
}

::v-deep .el-table .el-table__body-wrapper .el-table__body .el-table__row .el-table__cell:last-child .el-button:active {
  transition: none !important;
}
</style>